/**
 * Parses a Sabre hotel "About" HTML blob into a structured data object.
 *
 * @param {string} rawHtml
 * @param {object} opts
 * @param {string} [opts.name='Hotel']  - Hotel name (pass hotel.name from API)
 * @param {string} [opts.checkIn='']   - Check-in time override (from hotel.policies)
 * @param {string} [opts.checkOut='']  - Check-out time override (from hotel.policies)
 * @returns {{ name, location, checkIn, checkOut, shortDescription, sections }}
 */
/** CP1252 positions 0x80–0x9F → Unicode (reverse map for mojibake recovery). */
const UNICODE_TO_CP1252_BYTE = new Map([
  [0x20ac, 0x80],
  [0x201a, 0x82],
  [0x0192, 0x83],
  [0x201e, 0x84],
  [0x2026, 0x85],
  [0x2020, 0x86],
  [0x2021, 0x87],
  [0x02c6, 0x88],
  [0x2030, 0x89],
  [0x0160, 0x8a],
  [0x2039, 0x8b],
  [0x0152, 0x8c],
  [0x017d, 0x8e],
  [0x2018, 0x91],
  [0x2019, 0x92],
  [0x201c, 0x93],
  [0x201d, 0x94],
  [0x2022, 0x95],
  [0x2013, 0x96],
  [0x2014, 0x97],
  [0x02dc, 0x98],
  [0x2122, 0x99],
  [0x0161, 0x9a],
  [0x203a, 0x9b],
  [0x0153, 0x9c],
  [0x017e, 0x9e],
  [0x0178, 0x9f],
]);

/**
 * True when the string looks like UTF-8 bytes misread as Latin-1 or Windows-1252.
 * Avoids lone U+00E2 (â) in real French text (requires continuation-like bytes or €).
 */
function hasLikelyUtf8Mojibake(str) {
  if (!str) return false;
  if (/\u00c3[\u0080-\u00bf]/.test(str)) return true;
  if (/\u00c2[\u0080-\u00bf]/.test(str)) return true;
  if (/\u00e2[\u0080-\u00bf][\u0080-\u00bf]/.test(str)) return true;
  if (/\u00ef[\u0080-\u00bf][\u0080-\u00bf]/.test(str)) return true;
  // UTF-8 read as CP1252: â + € + third char (e.g. ™ for U+2019 apostrophe bytes)
  if (/\u00e2\u20ac[\u2122\u2018\u2019\u201c\u201d]/.test(str)) return true;
  return false;
}

/**
 * Maps each char to a byte: U+00–U+FF as-is, or CP1252 reverse for € / ™ / smart quotes.
 * @returns {Uint8Array|null} null if a non-mappable char appears
 */
function stringToMisinterpretedUtf8Bytes(str) {
  const out = [];
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code <= 0xff) {
      out.push(code);
      continue;
    }
    const b = UNICODE_TO_CP1252_BYTE.get(code);
    if (b === undefined) {
      return null;
    }
    out.push(b);
  }
  return new Uint8Array(out);
}

/**
 * Sabre / suppliers sometimes return UTF-8 decoded as ISO-8859-1 or Windows-1252.
 * Re-byte the string and decode as UTF-8. No-op unless mojibake patterns are detected.
 *
 * @param {string} str
 * @returns {string}
 */
export function fixMojibake(str) {
  if (!str || typeof str !== 'string') return str;
  if (!hasLikelyUtf8Mojibake(str)) {
    return str;
  }

  let bytes;
  let allLatin1 = true;
  for (let i = 0; i < str.length; i++) {
    if (str.charCodeAt(i) > 0xff) {
      allLatin1 = false;
      break;
    }
  }
  if (allLatin1) {
    bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      bytes[i] = str.charCodeAt(i);
    }
  } else {
    bytes = stringToMisinterpretedUtf8Bytes(str);
    if (!bytes) {
      return str;
    }
  }

  return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
}

export function parseBlob(rawHtml, { name = 'Hotel', checkIn = '', checkOut = '' } = {}) {
  if (!rawHtml) {
    return { name, location: '', checkIn, checkOut, shortDescription: '', sections: [] };
  }

  rawHtml = fixMojibake(rawHtml);

  // If caller passed a full HTML document, extract just the body
  const bodyMatch = rawHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const html = bodyMatch ? bodyMatch[1] : rawHtml;

  const tmp = document.createElement('div');
  tmp.innerHTML = html;

  const data = {
    name,
    location: '',
    checkIn,
    checkOut,
    shortDescription: '',
    sections: [],
  };

  let currentHeader = null;

  for (const el of tmp.children) {
    const cls = el.className || '';
    const tag = el.tagName;

    if (tag === 'BR') continue;
    if (cls.includes('font-bold')) continue; // "About" heading outside the blob

    // Short description — first .description before any section header
    if (cls.includes('description') && !currentHeader && !data.shortDescription) {
      data.shortDescription = fixMojibake(el.innerHTML);
      continue;
    }

    // Check-in / Check-out — unclassed divs containing a <b> label + time text
    if (!cls && !el.id && tag === 'DIV' && el.querySelector('b')) {
      const b = el.querySelector('b');
      const bText = b.textContent.toLowerCase();
      const time = el.textContent.replace(b.textContent, '').trim();
      if (bText.includes('check-in') || bText.includes('checkin')) data.checkIn = time;
      else if (bText.includes('check-out') || bText.includes('checkout')) data.checkOut = time;
      continue;
    }

    // Section header
    if (cls.includes('descriptionHeader')) {
      const b = el.querySelector('b');
      currentHeader = (b ? b.textContent : el.textContent).trim();
      continue;
    }

    // Section content — <pre> or <div class="description"> following a header
    if (currentHeader && (tag === 'PRE' || (tag === 'DIV' && cls.includes('description')))) {
      data.sections.push({ header: currentHeader, html: fixMojibake(el.innerHTML) });
      currentHeader = null;
    }
  }

  return data;
}
